import { FC, useEffect, useState } from 'react'

import {
	Alert,
	Box,
	Button,
	Flashbar,
	Header,
	Select,
	SpaceBetween,
	Table
} from '@cloudscape-design/components'
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces'
import { useNavigate, useSearchParams } from 'react-router-dom'

import LoadingSpinner from '../../../components/LoadingSpinner'
import useAllAdGroups from '../../../hooks/query/useAllAdGroups'
import { useFlashbarStateStore } from '../../../stores/notifications/flashbarStateStore'
import { AdGroup, AdGroupMember } from '../../../types/active-directory'

interface ActiveDirectoryGroupOption extends OptionDefinition {
	group: AdGroup
}

const ACTIVE_DIRECTORY_FLASHBAR_PREFIX = 'active-directory:'

const normalizeGroupName = (value: string): string =>
	value.trim().toLocaleLowerCase()

const buildNewMemberPagePath = (groupName?: string): string =>
	groupName
		? `/active-directory/new?groupName=${encodeURIComponent(groupName)}`
		: '/active-directory/new'

const ActiveDirectoryMembers: FC = () => {
	const navigate = useNavigate()
	const { data: groups, isLoading, error } = useAllAdGroups()
	const { items: flashbarItems } = useFlashbarStateStore()
	const [searchParams, setSearchParams] = useSearchParams()
	const [selectedGroup, setSelectedGroup] =
		useState<ActiveDirectoryGroupOption | null>(null)

	const requestedGroupName = searchParams.get('groupName') || ''
	const activeDirectoryFlashbarItems = flashbarItems.filter((item) =>
		item.id.startsWith(ACTIVE_DIRECTORY_FLASHBAR_PREFIX)
	)
	const safeGroups = Array.isArray(groups) ? groups : undefined

	const groupToOption = (group: AdGroup): ActiveDirectoryGroupOption => ({
		label: group.name,
		value: group.path,
		group
	})

	useEffect(() => {
		if (!requestedGroupName || !safeGroups?.length) {
			return
		}

		const requestedGroup = safeGroups.find(
			(group) =>
				normalizeGroupName(group.name) ===
				normalizeGroupName(requestedGroupName)
		)

		if (!requestedGroup) {
			return
		}

		if (
			selectedGroup &&
			normalizeGroupName(selectedGroup.group.name) ===
				normalizeGroupName(requestedGroup.name)
		) {
			return
		}

		setSelectedGroup(groupToOption(requestedGroup))
	}, [requestedGroupName, safeGroups, selectedGroup])

	const handleGroupSelection = (
		nextSelectedGroup: ActiveDirectoryGroupOption | null
	) => {
		setSelectedGroup(nextSelectedGroup)

		if (nextSelectedGroup?.group.name) {
			setSearchParams(
				{ groupName: nextSelectedGroup.group.name },
				{ replace: true }
			)
			return
		}

		setSearchParams({}, { replace: true })
	}

	const tableColumnDefinitions = [
		{
			id: 'name',
			header: 'Name',
			cell: (member: AdGroupMember) => <p>{member.name}</p>
		},
		{
			id: 'userId',
			header: 'User ID',
			cell: (member: AdGroupMember) => <p>{member.userId}</p>
		}
	]

	if (isLoading) return <LoadingSpinner />

	return (
		<SpaceBetween direction="vertical" size="m">
			<>
				{activeDirectoryFlashbarItems.length > 0 && (
					<Flashbar items={activeDirectoryFlashbarItems} />
				)}

				{error && (
					<Alert
						type="error"
						header="Failed to load Active Directory groups"
						statusIconAriaLabel="Error"
					>
						{error instanceof Error ? error.message : 'Unknown error'}
					</Alert>
				)}

				<Table
					header={
						<Header
							variant="h2"
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<div style={{ width: '300px' }}>
										<Select
											selectedOption={selectedGroup}
											onChange={({ detail }) =>
												handleGroupSelection(
													detail.selectedOption as ActiveDirectoryGroupOption | null
												)
											}
											options={(safeGroups || []).map(groupToOption)}
											filteringType="auto"
											placeholder="Select a group"
											ariaLabel="Filter by Group"
										/>
									</div>
									<Button onClick={() => navigate('/active-directory')}>
										Browse Groups
									</Button>
									<Button
										variant="primary"
										onClick={() =>
											navigate(
												buildNewMemberPagePath(selectedGroup?.group.name)
											)
										}
									>
										New Member
									</Button>
								</SpaceBetween>
							}
						>
							Active Directory Group Members
						</Header>
					}
					columnDefinitions={tableColumnDefinitions}
					items={selectedGroup?.group.members || []}
					sortingDisabled
					loading={isLoading}
					empty={
						selectedGroup ? (
							<Box textAlign="center" color="inherit">
								<b>No members found</b>
								<Box padding={{ bottom: 's' }} variant="p" color="inherit">
									This group does not have any members yet.
								</Box>
							</Box>
						) : (
							<Box textAlign="center" color="inherit">
								<b>No group selected</b>
								<Box padding={{ bottom: 's' }} variant="p" color="inherit">
									Select a group from the dropdown to view members.
								</Box>
							</Box>
						)
					}
				/>
			</>
		</SpaceBetween>
	)
}

export default ActiveDirectoryMembers
